/**
 * EnvDoctorPage — Diagnostic complet de l'environnement Assistia (S5.4-14/15)
 *
 * Consomme GET /api/v1/admin/health/deep via sdk.admin.getHealthDeep().
 * Affiche le statut de chaque composant (database, llm, storage, stream)
 * avec badge couleur, latence et détail d'erreur.
 * Bouton "Re-tester" — relance le diagnostic à la demande.
 *
 * Contrainte : zéro boucle for/while.
 */
import React, { useCallback, useEffect, useState } from 'react';
import {
  CheckCircle2,
  AlertTriangle,
  XCircle,
  RefreshCw,
  Database,
  Cpu,
  HardDrive,
  Activity,
} from 'lucide-react';
import { useSDK } from '../context/SDKContext.tsx';
import type { HealthDeep, HealthCheck } from '@assistia/sdk';

// ── Icônes par composant ──────────────────────────────────────────────────────

const COMPONENT_ICONS: Record<string, React.ReactElement> = {
  database: <Database size={16} />,
  llm:      <Cpu size={16} />,
  storage:  <HardDrive size={16} />,
  stream:   <Activity size={16} />,
};

// ── Badge statut ──────────────────────────────────────────────────────────────

function StatusBadge({ status }: { status: HealthCheck['status'] }): React.ReactElement {
  const config = {
    ok:       { icon: <CheckCircle2 size={14} />, label: 'OK',      cls: 'text-green-600 bg-green-500/10' },
    degraded: { icon: <AlertTriangle size={14} />, label: 'Dégradé', cls: 'text-orange-500 bg-orange-500/10' },
    down:     { icon: <XCircle size={14} />,      label: 'Indispo',  cls: 'text-destructive bg-destructive/10' },
  }[status];

  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium ${config.cls}`}>
      {config.icon}
      {config.label}
    </span>
  );
}

// ── Barre de statut globale ───────────────────────────────────────────────────

function GlobalStatusBanner({ status }: { status: HealthDeep['status'] }): React.ReactElement {
  const config = {
    ok:       { label: 'Tous les composants sont opérationnels.',    cls: 'bg-green-500/10 border-green-500/30 text-green-700' },
    degraded: { label: 'Un ou plusieurs composants sont dégradés.', cls: 'bg-orange-500/10 border-orange-500/30 text-orange-600' },
    down:     { label: 'Un composant critique est indisponible.',    cls: 'bg-destructive/10 border-destructive/30 text-destructive' },
  }[status];

  return (
    <div className={`rounded-lg border px-4 py-3 text-sm font-medium ${config.cls}`}>
      {config.label}
    </div>
  );
}

// ── Page ──────────────────────────────────────────────────────────────────────

export default function EnvDoctorPage(): React.ReactElement {
  const { sdk, isSimulated } = useSDK();
  const [health, setHealth] = useState<HealthDeep | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const runDiagnostic = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const { data } = await sdk.admin.getHealthDeep();
      setHealth(data);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  }, [sdk]);

  // Lancement auto au montage
  useEffect(() => { void runDiagnostic(); }, [runDiagnostic]);

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-foreground">Env Doctor</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Diagnostic complet des composants Assistia
            {isSimulated && <span className="ml-2 text-orange-500">(simulation)</span>}
          </p>
        </div>
        <button
          onClick={() => { void runDiagnostic(); }}
          disabled={loading}
          className="inline-flex items-center gap-2 px-4 py-2 text-sm rounded-md bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 transition-colors"
        >
          <RefreshCw size={14} className={loading ? 'animate-spin' : ''} />
          {loading ? 'Diagnostic en cours…' : 'Re-tester'}
        </button>
      </div>

      {error && (
        <div className="rounded-md bg-destructive/10 p-4 text-sm text-destructive">{error}</div>
      )}

      {loading && !health && (
        <div className="text-sm text-muted-foreground">Diagnostic en cours…</div>
      )}

      {health && (
        <>
          <GlobalStatusBanner status={health.status} />

          <div className="rounded-lg border border-border bg-card shadow-sm overflow-hidden">
            <table className="w-full text-sm">
              <thead className="bg-muted/50">
                <tr className="text-left text-muted-foreground">
                  <th className="px-4 py-3 font-medium">Composant</th>
                  <th className="px-4 py-3 font-medium">Statut</th>
                  <th className="px-4 py-3 font-medium">Latence</th>
                  <th className="px-4 py-3 font-medium">Détail</th>
                </tr>
              </thead>
              <tbody>
                {Object.entries(health.checks).map(([name, check]) => (
                  <tr key={name} className="border-t border-border hover:bg-muted/20 transition-colors">
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-2 font-medium text-foreground capitalize">
                        <span className="text-muted-foreground">
                          {COMPONENT_ICONS[name] ?? <Activity size={16} />}
                        </span>
                        {name}
                      </div>
                    </td>
                    <td className="px-4 py-3">
                      <StatusBadge status={check.status} />
                    </td>
                    <td className="px-4 py-3 text-muted-foreground">
                      {check.latency_ms !== undefined ? `${check.latency_ms} ms` : '—'}
                    </td>
                    <td className="px-4 py-3 text-muted-foreground font-mono text-xs">
                      {check.detail ?? '—'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          <p className="text-xs text-muted-foreground">
            Vérifié le {new Date(health.checked_at).toLocaleString('fr-FR')}
          </p>
        </>
      )}
    </div>
  );
}
